import React, { useCallback } from 'react'
import Panel from 'app/components/Panel'
import { List, IconButton } from '@material-ui/core'
import GroupItem from 'app/components/GroupItem'
import GroupAdd from '@material-ui/icons/GroupAdd'
import { makeStyles } from '@material-ui/core/styles'
import { FormattedMessage, useIntl } from 'react-intl'
import effects from 'app/effects/effects'
import ApiPanel from './ApiPanel'
import { map, compose } from 'ramda'
import { groupItemMapper } from './ItemMapper'
import { either, isNil, isEmpty } from 'ramda'
import EmptyContent from 'app/components/empty/EmptyContent'
import { withRouter, Link } from 'react-router-dom'
import { Routes } from 'app/services/routeService/routeService'
import { formatHomelessGroup } from 'commons/groupLogic'
import { arrayAsHookDependencies } from 'libs/hook'
import clsx from 'clsx'
import { getLocale } from 'app/translations/intl-context'
import * as R from 'ramda'
import { groupWithSubscriberType } from 'app/main/notification/selectRecipient/groupAdjuster'
import {
  PLACE_INVITATION_ACCEPT_INVITATION,
  PLACE_INVITATION_ACCEPT_REQUEST,
  PLACE_INVITATION_PUBLIC_SUBSCRIBE,
} from 'app/configs/statusConfigs'

const DISPLAY_GROUP_IN_PANEL = 3
const GROUP_EVERYONE = 'Everyone'

const panelParseResponseGroups = (formatMessage, itemMapper) => (response) => {
  const filterOutEveryone = response.groups.filter(
    (item) => item.name !== GROUP_EVERYONE,
  )
  return {
    items: map(
      compose(itemMapper, formatHomelessGroup(formatMessage)),
      filterOutEveryone,
    ),
  }
}

const apiFetcher = (formatMessage) => () =>
  effects.apis
    .getGroupWithSubscriber(
      R.mergeRight(
        R.objOf(
          'type',
          R.join(',', [
            groupWithSubscriberType.NORMAL,
            groupWithSubscriberType.HOME_LESS_GROUP,
          ]),
        ),
        {
          status: [
            PLACE_INVITATION_ACCEPT_REQUEST,
            PLACE_INVITATION_ACCEPT_INVITATION,
            PLACE_INVITATION_PUBLIC_SUBSCRIBE,
          ].join(','),
        },
      ),
    )
    .then(panelParseResponseGroups(formatMessage, groupItemMapper))

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: theme.palette.background.paper,
  },
}))

function GroupsPanel(props) {
  const { items, onClickItem, groupsPageSection } = props
  const classes = useStyles()
  const { formatMessage } = useIntl()
  return groupsPageSection ? (
    <>
      {either(isNil, isEmpty)(items) ? (
        <div className="w-full flex justify-center items-center">
          <EmptyContent
            linkProp={{ to: Routes.groups() }}
            linkLabel={formatMessage({ id: 'empty.group.link' })}
            descriptionLabel={formatMessage({ id: 'empty.group.desc' })}
          />
        </div>
      ) : (
        <List className={clsx(classes.root, groupsPageSection && 'flex')}>
          {items.slice(0, DISPLAY_GROUP_IN_PANEL).map((item) => (
            <GroupItem key={item.id} item={item} onClickItem={onClickItem} />
          ))}
        </List>
      )}
    </>
  ) : (
    <Panel
      startText={<FormattedMessage id="group.panelTitle" />}
      endView={
        <Link to={Routes.groups()}>
          <IconButton color="secondary">
            <GroupAdd />
          </IconButton>
        </Link>
      }
      minHeight={190}
    >
      {either(isNil, isEmpty)(items) ? (
        <div className="w-full flex justify-center items-center">
          <EmptyContent
            linkProp={{ to: Routes.groups() }}
            linkLabel={<FormattedMessage id="empty.group.link" />}
            descriptionLabel={<FormattedMessage id="empty.group.desc" />}
          />
        </div>
      ) : (
        <List className={classes.root}>
          {items.slice(0, DISPLAY_GROUP_IN_PANEL).map((item) => (
            <GroupItem key={item.id} item={item} onClickItem={onClickItem} />
          ))}
        </List>
      )}
    </Panel>
  )
}

export default withRouter(({ history, groups, groupsPageSection }) => {
  const { formatMessage } = useIntl()
  const locale = getLocale()

  const fetchGroups = useCallback(apiFetcher(formatMessage), [
    arrayAsHookDependencies(groups),
    locale,
  ])
  const onSuccess = (values) => {
    return (
      <GroupsPanel
        items={values.items}
        remainingCount={values.remainingCount}
        onClickItem={(group) => history.push(Routes.viewGroups(group.id))}
        groupsPageSection={groupsPageSection ? true : false}
      />
    )
  }
  return (
    <ApiPanel
      title={<FormattedMessage id="group.panelTitle" />}
      apiFetcher={fetchGroups}
      onSuccess={onSuccess}
    />
  )
})
